function nowInTimeZone(timeZone) {
  return new Date(new Date().toLocaleString('en-US', { timeZone }));
}

function formatYmd(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function computeEasterDate(year) {
  // Anonymous Gregorian algorithm
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=Mar, 4=Apr
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function getItalianHolidaySet(year) {
  const fixedDates = [
    `${year}-01-01`, // Capodanno
    `${year}-01-06`, // Epifania
    `${year}-04-25`, // Liberazione
    `${year}-05-01`, // Festa del Lavoro
    `${year}-06-02`, // Festa della Repubblica
    `${year}-08-15`, // Ferragosto
    `${year}-11-01`, // Ognissanti
    `${year}-12-08`, // Immacolata
    `${year}-12-25`, // Natale
    `${year}-12-26`, // Santo Stefano
  ];

  const easterSunday = computeEasterDate(year);
  const easterMonday = new Date(easterSunday);
  easterMonday.setDate(easterMonday.getDate() + 1);

  fixedDates.push(formatYmd(easterMonday)); // Pasquetta
  return new Set(fixedDates);
}

function isItalianHoliday(date) {
  const year = date.getFullYear();
  const holidaysThisYear = getItalianHolidaySet(year);
  const dateKey = formatYmd(date);
  if (holidaysThisYear.has(dateKey)) return true;

  // Safety for year-boundary calculations
  const holidaysNextYear = getItalianHolidaySet(year + 1);
  return holidaysNextYear.has(dateKey);
}

function isBusinessDay(date) {
  const day = date.getDay();
  return day >= 1 && day <= 5 && !isItalianHoliday(date);
}

function addBusinessDays(date, days) {
  const next = new Date(date);
  let added = 0;

  while (added < days) {
    next.setDate(next.getDate() + 1);
    if (isBusinessDay(next)) added += 1;
  }

  return next;
}

function nextBusinessDay(date) {
  const next = new Date(date);
  do {
    next.setDate(next.getDate() + 1);
  } while (!isBusinessDay(next));
  return next;
}

function pad(value) {
  return String(value).padStart(2, '0');
}

function formatRemaining(ms) {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${pad(hours)}:${pad(minutes)}`;
}

function formatRemainingNatural(ms) {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    const hourLabel = hours === 1 ? 'ora' : 'ore';
    const minuteLabel = minutes === 1 ? 'minuto' : 'minuti';
    return `${hours} ${hourLabel} e ${minutes} ${minuteLabel}`;
  }

  if (hours > 0) {
    const hourLabel = hours === 1 ? 'ora' : 'ore';
    return `${hours} ${hourLabel}`;
  }

  const minuteLabel = minutes === 1 ? 'minuto' : 'minuti';
  return `${minutes} ${minuteLabel}`;
}

function formatDateIt(date, timeZone) {
  return new Intl.DateTimeFormat('it-IT', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(date);
}

function computeEstimate(widget) {
  const timeZone = widget.dataset.timezone || 'Europe/Rome';
  const cutoffHour = Number.parseInt(widget.dataset.cutoffHour || '11', 10);
  const transitBusinessDays = Number.parseInt(widget.dataset.transitBusinessDays || '2', 10);

  const now = nowInTimeZone(timeZone);
  const todayCutoff = new Date(now);
  todayCutoff.setHours(cutoffHour, 0, 0, 0);

  const beforeCutoff = isBusinessDay(now) && now < todayCutoff;
  let cutoffDate = new Date(todayCutoff);
  if (!beforeCutoff) {
    const nextCutoffBase = nextBusinessDay(now);
    cutoffDate = new Date(nextCutoffBase);
    cutoffDate.setHours(cutoffHour, 0, 0, 0);
  }

  const shipDate = beforeCutoff ? new Date(now) : nextBusinessDay(now);
  const deliveryDate = addBusinessDays(shipDate, transitBusinessDays);

  return {
    beforeCutoff,
    remainingToCutoffMs: cutoffDate.getTime() - now.getTime(),
    deliveryDateLabel: formatDateIt(deliveryDate, timeZone),
  };
}

function renderWidget(widget) {
  const lead = widget.querySelector('[data-delivery-lead]');
  const timer = widget.querySelector('[data-delivery-timer]');
  const tail = widget.querySelector('[data-delivery-tail]');
  const note = widget.querySelector('[data-delivery-note]');
  if (
    !(lead instanceof HTMLElement) ||
    !(timer instanceof HTMLElement) ||
    !(tail instanceof HTMLElement) ||
    !(note instanceof HTMLElement)
  ) {
    return;
  }

  const estimate = computeEstimate(widget);
  const remaining = formatRemainingNatural(estimate.remainingToCutoffMs);
  const showUrgencyTimer = estimate.beforeCutoff && estimate.remainingToCutoffMs > 0 && estimate.remainingToCutoffMs <= 8 * 60 * 60 * 1000;

  if (showUrgencyTimer) {
    lead.textContent = 'Ordina entro';
    timer.textContent = remaining;
    timer.style.display = '';
    tail.textContent = `per ricevere il prodotto entro ${estimate.deliveryDateLabel}.`;
  } else {
    lead.textContent = 'Ordina oggi';
    timer.textContent = '';
    timer.style.display = 'none';
    tail.textContent = `per ricevere il prodotto entro ${estimate.deliveryDateLabel}.`;
  }

  note.textContent = 'Consegna stimata in 48h lavorative (sabato, domenica e festivi esclusi).';
}

function renderAll() {
  document.querySelectorAll('[data-delivery-estimate]').forEach((widget) => {
    if (!(widget instanceof HTMLElement)) return;
    renderWidget(widget);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderAll);
} else {
  renderAll();
}

setInterval(renderAll, 30000);
